import * as React from 'react';
import { LabelsContainer } from '../LabelsContainer';
import { Features } from './Features';
import { useSelector } from 'react-redux';
import {
  getNewbuildingImagesByIdSelector,
  getNewbuildingNameByIdSelector,
  getNewbuildingUrlByIdSelector,
} from '../../selectors/newbuilding';
import { Image } from '../../components/Image';
import { CarouselBlock } from '../../components/CarouselBlock';
import { Carousel } from '../../components/Carousel';
import { IImageInfo } from '../../types/newbuilding';
import { ISlideProps } from '../../components/Carousel/Carousel';
import { Thumbnails } from '../../components/Thumbnails';
import { NoImage } from '../../components/NoImage';
import { TFirstScreenImageMode } from '../../utils/lcp';

interface ICarouselProps {
  newbuildingId: number;
  isLeadFactory?: boolean;
  /**
   * Режим загрузки видимого (главного) слайда: `eager` — в SSR без lazy, `high` — ещё и с fetchPriority="high".
   * Без него все слайды lazy и появляются после гидрации.
   */
  firstScreenImage?: TFirstScreenImageMode;
}

export const CarouselContainer = ({ newbuildingId, isLeadFactory, firstScreenImage }: ICarouselProps) => {
  const newbuildingImages = useSelector(getNewbuildingImagesByIdSelector(newbuildingId));
  const name = useSelector(getNewbuildingNameByIdSelector(newbuildingId));
  const url = useSelector(getNewbuildingUrlByIdSelector(newbuildingId));
  // Карусель открывается на слайде с isDefault-фото; именно он виден на первом экране
  const defaultSlideIndex = Math.max(
    0,
    (newbuildingImages || []).findIndex((img: IImageInfo) => img.isDefault),
  );

  if (!newbuildingImages || !newbuildingImages.length) {
    return (
      <CarouselBlock
        features={<Features newbuildingId={newbuildingId} />}
        labels={<LabelsContainer offerId={newbuildingId} />}
      >
        <NoImage newbuildingId={newbuildingId} />
      </CarouselBlock>
    );
  }

  return (
    <CarouselBlock
      features={<Features newbuildingId={newbuildingId} />}
      labels={<LabelsContainer offerId={newbuildingId} />}
      thumbnails={isLeadFactory ? undefined : <Thumbnails images={newbuildingImages} />}
      url={url}
    >
      <Carousel
        slides={newbuildingImages.map(
          (img: IImageInfo, index: number) =>
            ({ visible }: ISlideProps) =>
              visible && (
                <Image
                  alt={`${index + 1}-я Фотография ${name}`}
                  src={img.thumbnailUrl}
                  lazy={!(firstScreenImage && index === defaultSlideIndex)}
                  fetchPriority={firstScreenImage === 'high' && index === defaultSlideIndex ? 'high' : undefined}
                  objectFit="cover"
                />
              ),
        )}
        defaultSlideIndex={defaultSlideIndex}
        hasThumbnails={!isLeadFactory && newbuildingImages.length > 3}
      />
    </CarouselBlock>
  );
};
